import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import { useRealtime } from "./realtime";
import type { RealtimePayload, User } from "./types";

type IncomingCall = {
  callId: string;
  roomSlug: string;
  fromUserId: string;
  fromUsername?: string;
  fromDisplayName?: string;
};

export type VoiceParticipant = {
  id: string;
  name: string;
  self?: boolean;
  connected?: boolean;
};

export type VoiceSession = {
  mode: "direct" | "group";
  channelSlug: string;
  channelName?: string;
  callId?: string;
  peerUserId?: string;
  peerDisplayName?: string;
  direction?: "incoming" | "outgoing";
  status: "ringing" | "joining" | "ready" | "connecting" | "connected";
  participants: VoiceParticipant[];
  muted: boolean;
  error?: string;
};

export type VoiceRoomState = {
  active: boolean;
  members: string[];
};

export type VoiceDevice = {
  deviceId: string;
  label: string;
};

type VoiceContextValue = {
  incoming: IncomingCall | null;
  session: VoiceSession | null;
  roomStates: Record<string, VoiceRoomState>;
  startDirectCall: (user: User) => Promise<void>;
  acceptIncomingCall: () => Promise<void>;
  rejectIncomingCall: (reason?: "declined" | "missed") => void;
  joinGroupCall: (slug: string, name?: string) => Promise<void>;
  leaveCall: () => void;
  toggleMute: () => void;
  inputDevices: VoiceDevice[];
  outputDevices: VoiceDevice[];
  inputDeviceId: string;
  outputDeviceId: string;
  refreshDevices: () => Promise<void>;
  setInputDevice: (deviceId: string) => Promise<void>;
  setOutputDevice: (deviceId: string) => Promise<void>;
  supportsOutputSelection: boolean;
};

type VoiceConfig = {
  stunUrl?: string;
  turnUrl?: string;
  turnUrls?: string[];
  turnUsername?: string;
  turnCredential?: string;
};

const VoiceContext = createContext<VoiceContextValue | null>(null);

function callPayload(payload: RealtimePayload): IncomingCall | null {
  const callId = String(payload.callId || "");
  const roomSlug = String(payload.roomSlug || "");
  const fromUserId = String(payload.fromUserId || "");
  if (!callId || !roomSlug || !fromUserId) return null;
  return {
    callId,
    roomSlug,
    fromUserId,
    fromUsername: String(payload.fromUsername || ""),
    fromDisplayName: String(payload.fromDisplayName || "")
  };
}

function directVoiceSlug(currentUsername: string | undefined, otherUsername: string) {
  const parts = [currentUsername, otherUsername]
    .map(value => String(value || "").trim().replace(/^@/, "").toLowerCase())
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index)
    .sort();
  const key = parts.join("-").replace(/[^a-z0-9_-]/g, "-") || "unknown";
  return `dm-${key}`;
}

export function VoiceProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const realtime = useRealtime();
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [roomStates, setRoomStates] = useState<Record<string, VoiceRoomState>>({});
  const [inputDevices, setInputDevices] = useState<VoiceDevice[]>([]);
  const [outputDevices, setOutputDevices] = useState<VoiceDevice[]>([]);
  const [inputDeviceId, setInputDeviceId] = useState("");
  const [outputDeviceId, setOutputDeviceId] = useState("");
  const sessionRef = useRef<VoiceSession | null>(null);
  const inputDeviceIdRef = useRef("");
  const outputDeviceIdRef = useRef("");
  const localStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef(new Map<string, RTCPeerConnection>());
  const audioRef = useRef(new Map<string, HTMLAudioElement>());
  const pendingIceRef = useRef(new Map<string, RTCIceCandidateInit[]>());
  const remoteReadyRef = useRef(new Set<string>());
  const configRef = useRef<RTCConfiguration | null>(null);
  const missedTimerRef = useRef<number | undefined>(undefined);

  const supportsOutputSelection = typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

  useEffect(() => {
    inputDeviceIdRef.current = inputDeviceId;
  }, [inputDeviceId]);

  useEffect(() => {
    outputDeviceIdRef.current = outputDeviceId;
  }, [outputDeviceId]);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    setInputDevices(devices.filter(device => device.kind === "audioinput").map(device => ({ deviceId: device.deviceId, label: device.label || "Microphone" })));
    setOutputDevices(devices.filter(device => device.kind === "audiooutput").map(device => ({ deviceId: device.deviceId, label: device.label || "Speaker" })));
  }, []);

  useEffect(() => {
    void refreshDevices();
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return;
    const onDeviceChange = () => void refreshDevices();
    mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => mediaDevices.removeEventListener("devicechange", onDeviceChange);
  }, [refreshDevices]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const updateSession = useCallback((update: Partial<VoiceSession>) => {
    setSession(current => current ? { ...current, ...update } : current);
  }, []);

  const loadRtcConfig = useCallback(async () => {
    if (configRef.current) return configRef.current;
    const config = await api<VoiceConfig>("/voice/config");
    const iceServers: RTCIceServer[] = [];
    if (config.stunUrl) iceServers.push({ urls: config.stunUrl });
    const turnUrls = config.turnUrls?.length ? config.turnUrls : config.turnUrl ? [config.turnUrl] : [];
    if (turnUrls.length) {
      iceServers.push({
        urls: turnUrls,
        username: config.turnUsername,
        credential: config.turnCredential
      });
    }
    configRef.current = { iceServers };
    return configRef.current;
  }, []);

  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not support microphone calls.");
    }
    const audio = inputDeviceIdRef.current ? { deviceId: { exact: inputDeviceIdRef.current } } : true;
    const stream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
    localStreamRef.current = stream;
    (window as Window & { __verseVoiceMicActive?: boolean }).__verseVoiceMicActive = true;
    window.dispatchEvent(new CustomEvent("versecomms:voice-mic-state", { detail: { active: true } }));
    void refreshDevices();
    return stream;
  }, [refreshDevices]);

  const applyOutputDevice = useCallback(async (audio: HTMLAudioElement, deviceId: string) => {
    const setSinkId = (audio as HTMLAudioElement & { setSinkId?: (nextDeviceId: string) => Promise<void> }).setSinkId;
    if (typeof setSinkId === "function") await setSinkId.call(audio, deviceId);
  }, []);

  const closePeer = useCallback((userId: string) => {
    peersRef.current.get(userId)?.close();
    peersRef.current.delete(userId);
    remoteReadyRef.current.delete(userId);
    pendingIceRef.current.delete(userId);
    const audio = audioRef.current.get(userId);
    if (audio) {
      audio.pause();
      audio.srcObject = null;
      audio.remove();
    }
    audioRef.current.delete(userId);
  }, []);

  const closeMedia = useCallback(() => {
    [...peersRef.current.keys()].forEach(closePeer);
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    localStreamRef.current = null;
    (window as Window & { __verseVoiceMicActive?: boolean }).__verseVoiceMicActive = false;
    window.dispatchEvent(new CustomEvent("versecomms:voice-mic-state", { detail: { active: false } }));
  }, [closePeer]);

  const resolveParticipantNames = useCallback(async (ids: string[]) => {
    const remoteIds = ids.filter(id => id && id !== auth.user?.id);
    if (!remoteIds.length) return;
    try {
      const presence = await api<Array<{ userId: string; username?: string; displayName?: string }>>(
        `/presence?userIds=${encodeURIComponent(remoteIds.join(","))}`
      );
      setSession(current => {
        if (!current) return current;
        return {
          ...current,
          participants: current.participants.map(participant => {
            const match = presence.find(item => item.userId === participant.id);
            return match
              ? { ...participant, name: match.displayName || (match.username ? `@${match.username}` : participant.name) }
              : participant;
          })
        };
      });
    } catch {
      // IDs remain useful when presence lookup is unavailable.
    }
  }, [auth.user?.id]);

  const ensurePeer = useCallback(async (remoteUserId: string, channelSlug: string) => {
    const existing = peersRef.current.get(remoteUserId);
    if (existing) return existing;
    const [configuration, stream] = await Promise.all([loadRtcConfig(), ensureLocalStream()]);
    const peer = new RTCPeerConnection(configuration);
    stream.getTracks().forEach(track => peer.addTrack(track, stream));
    peer.onicecandidate = event => {
      const candidate = event.candidate;
      if (!candidate) return;
      realtime.send({
        type: "voice_channel_signal",
        channelSlug,
        toUserId: remoteUserId,
        signalType: "ice",
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex
      });
    };
    peer.ontrack = event => {
      let audio = audioRef.current.get(remoteUserId);
      if (!audio) {
        audio = new Audio();
        audio.autoplay = true;
        audioRef.current.set(remoteUserId, audio);
      }
      audio.srcObject = event.streams[0];
      void applyOutputDevice(audio, outputDeviceIdRef.current);
      void audio.play().catch(() => undefined);
    };
    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      if (state === "connected") {
        updateSession({ status: "connected" });
        setSession(current => current ? {
          ...current,
          participants: current.participants.map(item =>
            item.id === remoteUserId ? { ...item, connected: true } : item
          )
        } : current);
      } else if (state === "failed" || state === "closed") {
        closePeer(remoteUserId);
      } else if (state === "connecting") {
        updateSession({ status: "connecting" });
      }
    };
    peersRef.current.set(remoteUserId, peer);
    return peer;
  }, [applyOutputDevice, closePeer, ensureLocalStream, loadRtcConfig, realtime, updateSession]);

  const flushPendingIce = useCallback(async (remoteUserId: string, peer: RTCPeerConnection) => {
    const pending = pendingIceRef.current.get(remoteUserId) || [];
    pendingIceRef.current.delete(remoteUserId);
    for (const candidate of pending) {
      await peer.addIceCandidate(candidate).catch(() => undefined);
    }
  }, []);

  const sendOffer = useCallback(async (remoteUserId: string, channelSlug: string) => {
    const peer = await ensurePeer(remoteUserId, channelSlug);
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    realtime.send({
      type: "voice_channel_signal",
      channelSlug,
      toUserId: remoteUserId,
      signalType: "offer",
      sdp: offer.sdp
    });
  }, [ensurePeer, realtime]);

  const handleSignal = useCallback(async (payload: RealtimePayload) => {
    const current = sessionRef.current;
    const remoteUserId = String(payload.fromUserId || "");
    const channelSlug = String(payload.channelSlug || "");
    const signalType = String(payload.signalType || "");
    if (!current || !remoteUserId || current.channelSlug !== channelSlug) return;
    const peer = await ensurePeer(remoteUserId, channelSlug);
    if (signalType === "offer" && payload.sdp) {
      await peer.setRemoteDescription({ type: "offer", sdp: String(payload.sdp) });
      remoteReadyRef.current.add(remoteUserId);
      await flushPendingIce(remoteUserId, peer);
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      realtime.send({
        type: "voice_channel_signal",
        channelSlug,
        toUserId: remoteUserId,
        signalType: "answer",
        sdp: answer.sdp
      });
    } else if (signalType === "answer" && payload.sdp) {
      await peer.setRemoteDescription({ type: "answer", sdp: String(payload.sdp) });
      remoteReadyRef.current.add(remoteUserId);
      await flushPendingIce(remoteUserId, peer);
    } else if (signalType === "ice" && payload.candidate) {
      const candidate: RTCIceCandidateInit = {
        candidate: String(payload.candidate),
        sdpMid: payload.sdpMid ? String(payload.sdpMid) : null,
        sdpMLineIndex: typeof payload.sdpMLineIndex === "number" ? payload.sdpMLineIndex : null
      };
      if (remoteReadyRef.current.has(remoteUserId)) {
        await peer.addIceCandidate(candidate).catch(() => undefined);
      } else {
        const pending = pendingIceRef.current.get(remoteUserId) || [];
        pending.push(candidate);
        pendingIceRef.current.set(remoteUserId, pending);
      }
    }
  }, [ensurePeer, flushPendingIce, realtime]);

  const leaveCall = useCallback(() => {
    const current = sessionRef.current;
    if (current) {
      if (current.mode === "direct" && current.peerUserId && current.callId) {
        realtime.send({
          type: "hangup",
          toUserId: current.peerUserId,
          callId: current.callId,
          roomSlug: current.channelSlug
        });
      }
      realtime.send({ type: "voice_channel_leave", slug: current.channelSlug });
    }
    closeMedia();
    sessionRef.current = null;
    setSession(null);
  }, [closeMedia, realtime]);

  const endRemoteCall = useCallback(() => {
    const current = sessionRef.current;
    if (current) realtime.send({ type: "voice_channel_leave", slug: current.channelSlug });
    closeMedia();
    sessionRef.current = null;
    setSession(null);
    setIncoming(null);
  }, [closeMedia, realtime]);

  const joinRoom = useCallback(async (next: VoiceSession) => {
    const current = sessionRef.current;
    if (current && current.channelSlug !== next.channelSlug) leaveCall();
    sessionRef.current = next;
    setSession(next);
    try {
      await ensureLocalStream();
      realtime.send({ type: "voice_channel_join", slug: next.channelSlug });
    } catch (error) {
      setSession(value => value ? {
        ...value,
        error: error instanceof Error ? error.message : "Microphone access failed.",
        status: "ready"
      } : value);
      throw error;
    }
  }, [ensureLocalStream, leaveCall, realtime]);

  const startDirectCall = useCallback(async (user: User) => {
    if (!user.id) return;
    const roomSlug = directVoiceSlug(auth.user?.username, user.username);
    const callId = roomSlug;
    await ensureLocalStream();
    realtime.send({ type: "call_invite", toUserId: user.id, callId, roomSlug });
    await joinRoom({
      mode: "direct",
      channelSlug: roomSlug,
      callId,
      peerUserId: user.id,
      peerDisplayName: user.displayName || user.username,
      direction: "outgoing",
      status: "ringing",
      participants: [
        { id: auth.user?.id || "self", name: auth.user?.displayName || auth.user?.username || "You", self: true },
        { id: user.id, name: user.displayName || `@${user.username}` }
      ],
      muted: false
    });
  }, [auth.user, ensureLocalStream, joinRoom, realtime]);

  const acceptIncomingCall = useCallback(async () => {
    const call = incoming;
    if (!call) return;
    await joinRoom({
      mode: "direct",
      channelSlug: call.roomSlug,
      callId: call.callId,
      peerUserId: call.fromUserId,
      peerDisplayName: call.fromDisplayName || call.fromUsername || "Pilot",
      direction: "incoming",
      status: "joining",
      participants: [
        { id: auth.user?.id || "self", name: auth.user?.displayName || auth.user?.username || "You", self: true },
        { id: call.fromUserId, name: call.fromDisplayName || (call.fromUsername ? `@${call.fromUsername}` : "Pilot") }
      ],
      muted: false
    });
    realtime.send({
      type: "call_accept",
      toUserId: call.fromUserId,
      callId: call.callId,
      roomSlug: call.roomSlug
    });
    setIncoming(null);
  }, [auth.user, incoming, joinRoom, realtime]);

  const rejectIncomingCall = useCallback((reason: "declined" | "missed" = "declined") => {
    if (!incoming) return;
    realtime.send({
      type: "call_reject",
      toUserId: incoming.fromUserId,
      callId: incoming.callId,
      roomSlug: incoming.roomSlug,
      reason
    });
    setIncoming(null);
  }, [incoming, realtime]);

  const joinGroupCall = useCallback(async (slug: string, name?: string) => {
    await joinRoom({
      mode: "group",
      channelSlug: slug,
      channelName: name || `#${slug}`,
      status: "joining",
      participants: [
        { id: auth.user?.id || "self", name: auth.user?.displayName || auth.user?.username || "You", self: true }
      ],
      muted: false
    });
  }, [auth.user, joinRoom]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !sessionRef.current?.muted;
    stream.getAudioTracks().forEach(track => { track.enabled = !nextMuted; });
    updateSession({ muted: nextMuted });
  }, [updateSession]);

  const setInputDevice = useCallback(async (deviceId: string) => {
    setInputDeviceId(deviceId);
    inputDeviceIdRef.current = deviceId;
    if (!localStreamRef.current) return;
    const audio = deviceId ? { deviceId: { exact: deviceId } } : true;
    const nextStream = await navigator.mediaDevices.getUserMedia({ audio, video: false });
    const nextTrack = nextStream.getAudioTracks()[0];
    if (!nextTrack) throw new Error("The selected microphone is unavailable.");
    nextTrack.enabled = !sessionRef.current?.muted;
    await Promise.all([...peersRef.current.values()].flatMap(peer => {
      const sender = peer.getSenders().find(item => item.track?.kind === "audio");
      return sender ? [sender.replaceTrack(nextTrack)] : [];
    }));
    localStreamRef.current.getTracks().forEach(track => track.stop());
    localStreamRef.current = nextStream;
    await refreshDevices();
  }, [refreshDevices]);

  const setOutputDevice = useCallback(async (deviceId: string) => {
    if (!supportsOutputSelection) throw new Error("This browser does not support speaker selection.");
    await Promise.all([...audioRef.current.values()].map(audio => applyOutputDevice(audio, deviceId)));
    setOutputDeviceId(deviceId);
    outputDeviceIdRef.current = deviceId;
  }, [applyOutputDevice, supportsOutputSelection]);

  useEffect(() => realtime.subscribe(payload => {
    if (payload.type === "call_invite") {
      const call = callPayload(payload);
      if (!call || sessionRef.current) return;
      setIncoming(call);
      window.clearTimeout(missedTimerRef.current);
      missedTimerRef.current = window.setTimeout(() => {
        setIncoming(current => {
          if (!current || current.callId !== call.callId) return current;
          realtime.send({
            type: "call_reject",
            toUserId: call.fromUserId,
            callId: call.callId,
            roomSlug: call.roomSlug,
            reason: "missed"
          });
          return null;
        });
      }, 60_000);
      return;
    }
    if (payload.type === "call_accept") {
      updateSession({ status: "joining" });
      return;
    }
    if (payload.type === "call_reject" || payload.type === "hangup") {
      const current = sessionRef.current;
      if (!current || !payload.callId || current.callId === String(payload.callId)) endRemoteCall();
      return;
    }
    if (payload.type === "voice_channel_state") {
      const slug = String(payload.channelSlug || "");
      if (!slug) return;
      const members = Array.isArray(payload.members) ? payload.members.map(String) : [];
      setRoomStates(current => ({ ...current, [slug]: { active: Boolean(payload.active), members } }));
      return;
    }
    if (payload.type === "voice_channel_joined") {
      const current = sessionRef.current;
      const slug = String(payload.channelSlug || "");
      if (!current || current.channelSlug !== slug) return;
      const members = Array.isArray(payload.members) ? payload.members.map(String) : [];
      const participants = members.map(id => ({
        id,
        name: id === auth.user?.id
          ? auth.user?.displayName || auth.user?.username || "You"
          : current.peerUserId === id
            ? current.peerDisplayName || "Pilot"
            : `Pilot ${id.slice(-4)}`,
        self: id === auth.user?.id
      }));
      setSession(value => value ? {
        ...value,
        status: members.length > 1 ? "connecting" : "ready",
        participants
      } : value);
      void resolveParticipantNames(members);
      members.filter(id => id !== auth.user?.id).forEach(id => void sendOffer(id, slug));
      return;
    }
    if (payload.type === "voice_channel_member_joined") {
      const current = sessionRef.current;
      const userId = String(payload.userId || "");
      const roomSlug = String(payload.roomId || "").replace(/^vc_/, "");
      if (!current || current.channelSlug !== roomSlug || !userId || userId === auth.user?.id) return;
      setSession(value => value ? {
        ...value,
        status: "connecting",
        participants: value.participants.some(item => item.id === userId)
          ? value.participants
          : [...value.participants, { id: userId, name: `Pilot ${userId.slice(-4)}` }]
      } : value);
      void resolveParticipantNames([userId]);
      return;
    }
    if (payload.type === "voice_channel_member_left") {
      const userId = String(payload.userId || "");
      closePeer(userId);
      setSession(value => value ? {
        ...value,
        status: peersRef.current.size ? value.status : "ready",
        participants: value.participants.filter(item => item.id !== userId)
      } : value);
      return;
    }
    if (payload.type === "voice_channel_left") {
      const current = sessionRef.current;
      if (current?.channelSlug === String(payload.channelSlug || "")) endRemoteCall();
      return;
    }
    if (payload.type === "voice_channel_signal") void handleSignal(payload);
  }), [
    auth.user,
    closePeer,
    endRemoteCall,
    handleSignal,
    realtime,
    resolveParticipantNames,
    sendOffer,
    updateSession
  ]);

  useEffect(() => () => {
    window.clearTimeout(missedTimerRef.current);
    closeMedia();
  }, [closeMedia]);

  const value = useMemo<VoiceContextValue>(() => ({
    incoming,
    session,
    roomStates,
    startDirectCall,
    acceptIncomingCall,
    rejectIncomingCall,
    joinGroupCall,
    leaveCall,
    toggleMute,
    inputDevices,
    outputDevices,
    inputDeviceId,
    outputDeviceId,
    refreshDevices,
    setInputDevice,
    setOutputDevice,
    supportsOutputSelection
  }), [
    acceptIncomingCall,
    incoming,
    joinGroupCall,
    leaveCall,
    rejectIncomingCall,
    roomStates,
    session,
    startDirectCall,
    toggleMute,
    inputDevices,
    outputDevices,
    inputDeviceId,
    outputDeviceId,
    refreshDevices,
    setInputDevice,
    setOutputDevice,
    supportsOutputSelection
  ]);

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function useVoice() {
  const value = useContext(VoiceContext);
  if (!value) throw new Error("useVoice must be used inside VoiceProvider");
  return value;
}
